import { Link } from 'react-router';
import { cx } from '../lib/cx.js';
import { Icon, type IconName } from '../components/icon.js';
import { useSession, useTimezone } from '../data/session.js';

/**
 * Every function as a card, on the screen you land on (PLAN §13.1).
 *
 * The bar at the bottom is for moving between places you already know. This
 * is the other thing: opening the app and *seeing* what it does. Seven
 * destinations existed and four were visible, and even now the three in the
 * header are 20px icons — legible once you know them, not an answer to "what
 * is in here". The line under each label is that answer.
 *
 * Phone only. The desktop rail already lists all of this, with words.
 */

interface AppCard {
  to: string;
  label: string;
  hint: string;
  icon: IconName;
  /**
   * The card's own colour (`--aw-hue-*` in tokens.css).
   *
   * Whole class names rather than something built from a hue, because
   * Tailwind reads the source as text: `bg-${hue}-soft` compiles to nothing
   * at all and the card renders transparent.
   */
  tint: string;
  ink: string;
}

/**
 * The primary stays ink. It is the one thing here that is not one of six, and
 * a seventh colour would say otherwise.
 */
const compose: AppCard = {
  to: '/compose',
  label: 'New post',
  hint: 'Upload once, publish everywhere',
  icon: 'plus',
  tint: 'bg-accent',
  ink: 'text-on-accent',
};

const destinations: AppCard[] = [
  {
    to: '/posts',
    label: 'Posts',
    hint: 'Drafts, scheduled, published',
    icon: 'posts',
    tint: 'bg-blue-soft',
    ink: 'text-blue',
  },
  {
    to: '/calendar',
    label: 'Calendar',
    hint: 'What goes out, and when',
    icon: 'calendar',
    tint: 'bg-amber-soft',
    ink: 'text-amber',
  },
  {
    to: '/media',
    label: 'Media',
    hint: 'Everything uploaded',
    icon: 'media',
    tint: 'bg-violet-soft',
    ink: 'text-violet',
  },
  {
    to: '/analytics',
    label: 'Analytics',
    hint: 'How posts did',
    icon: 'analytics',
    tint: 'bg-teal-soft',
    ink: 'text-teal',
  },
  {
    to: '/accounts',
    label: 'Accounts',
    hint: 'Connected handles',
    icon: 'accounts',
    tint: 'bg-rose-soft',
    ink: 'text-rose',
  },
  {
    to: '/settings',
    label: 'Settings',
    hint: 'Captions, defaults, archive',
    icon: 'settings',
    tint: 'bg-slate-soft',
    ink: 'text-slate',
  },
];

/**
 * Morning, afternoon or evening in the owner's own timezone — not the
 * browser's. The owner is in Kampala; a server in Germany or a trip is not a
 * reason to be told good evening at breakfast.
 */
function useGreeting(): { time: string; name: string | null } {
  const session = useSession();
  const timezone = useTimezone();

  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: timezone }).format(
      new Date(),
    ),
  );
  const time = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const full = session.data?.name ?? null;
  return { time, name: full ? (full.split(' ')[0] ?? full) : null };
}

export function AppGrid() {
  const { time, name } = useGreeting();

  return (
    <nav aria-label="All functions" className="mb-5 md:hidden">
      <div className="mb-4">
        <p className="font-display text-xl leading-tight text-ink">
          {time}
          {name ? `, ${name}` : ''}
        </p>
        <p className="mt-0.5 text-sm text-muted">What would you like to do?</p>
      </div>

      <ul className="grid grid-cols-2 gap-2">
        <li className="col-span-2">
          <Link
            to={compose.to}
            className={cx(
              'flex items-center gap-3 rounded-aw-lg p-4 transition-opacity active:opacity-80',
              compose.tint,
              compose.ink,
            )}
          >
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-aw-lg bg-on-accent/15">
              <Icon name={compose.icon} size={24} />
            </span>
            <span className="min-w-0">
              <span className="block text-base font-semibold">{compose.label}</span>
              <span className="mt-0.5 block text-xs text-on-accent/80">{compose.hint}</span>
            </span>
          </Link>
        </li>

        {destinations.map((card) => (
          <li key={card.to}>
            <Link
              to={card.to}
              className={cx(
                'flex h-full flex-col gap-2 rounded-aw-lg p-3.5 transition-opacity active:opacity-80',
                card.tint,
              )}
            >
              <span className={cx('grid h-9 w-9 place-items-center', card.ink)}>
                <Icon name={card.icon} size={22} />
              </span>
              <span className="min-w-0">
                {/* Ink and muted, not the hue: every one of these is asserted
                    against AA on its own tint in tokens.test.ts, and the
                    colour is identity rather than meaning (PLAN §13.1). */}
                <span className="block truncate text-base font-semibold text-ink">
                  {card.label}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-muted">{card.hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
